import { Move, Square } from "chess.js";
import { useGame } from "../context/GameContext";
import usePlayAudio from "../utils/audio/moveAudio";
import { useMultiplayer } from "../context/MultiplayerContext";

export default function useMoveHandler() {
    const { gameState, setGameState, promotion } = useGame();
    const { multiplayerState } = useMultiplayer();

    const playAudio = usePlayAudio();

    const handleMove = (move: Move) => {
        if (!gameState.game) return;
        gameState.game.move({
            from: move.from,
            to: move.to,
            promotion: promotion.type ?? undefined,
        });

        setGameState((prev) => ({
            ...prev,
            game: gameState.game,
            lastMove: {
                from: move.from,
                to: move.to,
            },
        }));
        playAudio();
    };

    /**
     * Determines if the player is allowed to make a move.
     * @returns true if the player is allowed to make a move, false otherwise.
     */
    const allowMove = (): boolean => {
        if (!gameState.game) {
            console.error("Game is null in allowMove.");
            return false;
        }

        if (promotion.open) return false;
        if (multiplayerState.isHost) return false;
        if (multiplayerState.forfeit.isForfeit) return false;
        if (gameState.game.turn() !== multiplayerState.playerColor) return false;

        return true;
    };

    /**
     * Handles the promotion of a pawn.
     * Opens the promotion dialog if the move is a promotion.
     * Changes the pending move to the promotion type to complete the move.
     *
     * @param sourceSquare the source square of the piece.
     * @param targetSquare the target square of the piece.
     */
    const getMove = (sourceSquare: Square, targetSquare: Square): Move | null => {
        if (!gameState.game) return null;

        try {
            // Get all possible moves from the source square
            const possibleMoves = gameState.game.moves({
                square: sourceSquare,
                verbose: true,
            });

            // Find the move that matches the target
            const matchingMove = possibleMoves.find((move) => move.to === targetSquare);

            if (!matchingMove) {
                console.warn(`No valid move from ${sourceSquare} to ${targetSquare}`);
                return null;
            }

            return matchingMove;
        } catch (err) {
            console.error("Move validation error:", err);
            return null;
        }
    };

    return {
        handleMove,
        getMove,
        allowMove,
    };
}
